import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Siren, AlertOctagon, ShieldAlert, HeartPulse, UserCheck } from 'lucide-react'
import { safetyService, SafetyEvent, MedevacBody } from '@/services/safetyService'

export default function SafetyPage() {
    const [open, setOpen] = useState<SafetyEvent[]>([])
    const [interval, setIntervalMin] = useState(30)
    const [med, setMed] = useState<MedevacBody>({})

    const loadOpen = () => safetyService.open().then(setOpen).catch(() => {})
    useEffect(() => { loadOpen() }, [])

    const withGeo = (fn: (r?: { lat?: number; lon?: number }) => Promise<any>, msg: string) => () => {
        const send = (r?: { lat?: number; lon?: number }) => fn(r).then(() => { toast.success(msg); loadOpen() }).catch(() => toast.error('No se pudo enviar'))
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (p) => send({ lat: p.coords.latitude, lon: p.coords.longitude }),
                () => send(),
                { timeout: 4000 })
        } else send()
    }

    const sendMedevac = async (e: React.FormEvent) => {
        e.preventDefault()
        try { await safetyService.medevac(med); toast.success('MEDEVAC enviado'); setMed({}) }
        catch { toast.error('No se pudo enviar el MEDEVAC') }
    }

    const inp = 'w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm'
    const card = 'bg-gray-800/40 border border-gray-700 rounded-lg p-4'

    return (
        <div className="p-6 text-gray-100">
            <div className="flex items-center gap-3 mb-6"><Siren className="text-red-400" /><h1 className="text-2xl font-bold">Seguridad</h1></div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Botones de emergencia */}
                <div className={card + ' space-y-3'}>
                    <h2 className="font-semibold">Emergencia</h2>
                    <button onClick={withGeo(safetyService.panic, 'SOS enviado')}
                        className="w-full bg-red-600 hover:bg-red-500 rounded px-3 py-4 text-lg font-bold flex items-center justify-center gap-2">
                        <AlertOctagon /> SOS / PÁNICO
                    </button>
                    <button onClick={withGeo(safetyService.manDown, 'Hombre caído reportado')}
                        className="w-full bg-amber-600 hover:bg-amber-500 rounded px-3 py-2 text-sm font-medium flex items-center justify-center gap-2">
                        <HeartPulse size={16} /> Hombre caído
                    </button>
                    <button onClick={withGeo(safetyService.duress, 'Coacción reportada')}
                        className="w-full bg-purple-600 hover:bg-purple-500 rounded px-3 py-2 text-sm font-medium flex items-center justify-center gap-2">
                        <ShieldAlert size={16} /> Coacción (silenciosa)
                    </button>
                </div>

                {/* Trabajador solitario */}
                <div className={card + ' space-y-3'}>
                    <h2 className="font-semibold flex items-center gap-2"><UserCheck size={16} /> Trabajador solitario</h2>
                    <div className="flex items-center gap-2">
                        <input type="number" className={inp} value={interval} onChange={(e) => setIntervalMin(parseInt(e.target.value))} />
                        <span className="text-xs text-gray-400 whitespace-nowrap">min/check-in</span>
                    </div>
                    <button onClick={() => safetyService.startLoneWorker(interval).then(() => toast.success('Sesión iniciada'))}
                        className="w-full bg-emerald-600 hover:bg-emerald-500 rounded px-3 py-2 text-sm font-medium">Iniciar sesión</button>
                    <button onClick={() => safetyService.checkin().then(() => toast.success('Check-in OK')).catch(() => toast.error('Sin sesión activa'))}
                        className="w-full bg-indigo-600 hover:bg-indigo-500 rounded px-3 py-2 text-sm font-medium">Check-in</button>
                    <button onClick={() => safetyService.endLoneWorker().then(() => toast.success('Sesión finalizada'))}
                        className="w-full bg-gray-700 hover:bg-gray-600 rounded px-3 py-2 text-sm font-medium">Finalizar</button>
                </div>

                {/* Eventos abiertos (mando) */}
                <div className={card}>
                    <h2 className="font-semibold mb-3">Eventos abiertos</h2>
                    <div className="space-y-2 max-h-72 overflow-y-auto">
                        {open.map((e) => (
                            <div key={e.id} className="bg-gray-900/50 rounded px-3 py-2 text-sm">
                                <div className="flex items-center justify-between">
                                    <span className="text-red-300 font-medium">{e.type}</span>
                                    <span className="text-xs text-gray-500">{new Date(e.createdAt).toLocaleTimeString()}</span>
                                </div>
                                <div className="text-gray-400 text-xs">{e.callsign || e.userId.slice(0, 8)} {e.detail ? `· ${e.detail}` : ''}</div>
                                <div className="flex gap-2 mt-1">
                                    <button onClick={() => safetyService.ack(e.id).then(loadOpen)} className="text-xs text-amber-400">Reconocer</button>
                                    <button onClick={() => safetyService.resolve(e.id).then(loadOpen)} className="text-xs text-emerald-400">Resolver</button>
                                </div>
                            </div>
                        ))}
                        {open.length === 0 && <p className="text-gray-500 text-sm">Sin eventos abiertos.</p>}
                    </div>
                </div>
            </div>

            {/* 9-Line MEDEVAC */}
            <form onSubmit={sendMedevac} className={card + ' mt-6'}>
                <h2 className="font-semibold mb-3">9-Line MEDEVAC</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <input className={inp} placeholder="L1 Lat" onChange={(e) => setMed({ ...med, lat: parseFloat(e.target.value) })} />
                    <input className={inp} placeholder="L1 Lon" onChange={(e) => setMed({ ...med, lon: parseFloat(e.target.value) })} />
                    <input className={inp} placeholder="L2 Frecuencia/indicativo" onChange={(e) => setMed({ ...med, line2FreqCallsign: e.target.value })} />
                    <input className={inp} placeholder="L3 Pacientes por precedencia" onChange={(e) => setMed({ ...med, line3PatientsPrecedence: e.target.value })} />
                    <input className={inp} placeholder="L4 Equipo especial" onChange={(e) => setMed({ ...med, line4SpecialEquipment: e.target.value })} />
                    <input className={inp} placeholder="L5 Pacientes por tipo" onChange={(e) => setMed({ ...med, line5PatientsType: e.target.value })} />
                    <input className={inp} placeholder="L6 Seguridad" onChange={(e) => setMed({ ...med, line6Security: e.target.value })} />
                    <input className={inp} placeholder="L7 Marcación" onChange={(e) => setMed({ ...med, line7MarkingMethod: e.target.value })} />
                    <input className={inp} placeholder="L8 Nacionalidad/estado" onChange={(e) => setMed({ ...med, line8NationalityStatus: e.target.value })} />
                    <input className={inp} placeholder="L9 NBC/terreno" onChange={(e) => setMed({ ...med, line9NbcTerrain: e.target.value })} />
                </div>
                <button className="mt-3 bg-red-600 hover:bg-red-500 rounded px-4 py-2 text-sm font-medium">Enviar MEDEVAC</button>
            </form>
        </div>
    )
}
